import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Menu } from 'primereact/menu';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import AddBatchModal from './AddBatchModal';

interface Batch {
  batch_id: string;
  institute_id: string;
  branch_id: string;
  course_id: string;
  batch_name: string;
  batch_time: string;
  start_month: string;
  start_year: number;
  end_month: string;
  end_year: number;
  is_active: boolean;
  created_at: string | null;
}

interface Course {
  course_id: string;
  name: string;
  title: string;
  fees_slab?: {
    fees_slab_id: string;
    base_fee: number;
    minimum_fee: number;
    maximum_fee: number;
    status: string;
    created_at?: string;
  } | null;
}

interface ApiResponse {
  success: boolean;
  data: Batch[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

const Batches: React.FC = () => {
  const toast = useRef<Toast>(null);
  const menuRefs = useRef<{ [key: string]: Menu | null }>({});
  const navigate = useNavigate();

  const [batches, setBatches] = useState<Batch[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState<Batch | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ visible: boolean; batchId: string; batchName: string }>(
    { visible: false, batchId: '', batchName: '' }
  );

  const { profile, loading: profileLoading } = useOperatorProfileStore();

  // ── Inject menu styles (navy/gold themed) ────────────────────────────────
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .batch-card .p-card-body { padding: 0 !important; }
      .batch-card .p-card-content { padding: 0 !important; }
      .p-menu {
        border-radius: 12px !important;
        border: 1px solid #E5E7EB !important;
        box-shadow: 0 8px 24px rgba(14,34,93,0.12) !important;
        padding: 8px !important;
      }
      .p-menu .p-menuitem-link {
        border-radius: 8px !important;
        padding: 10px 12px !important;
        margin: 2px 0 !important;
        transition: all 0.2s ease !important;
      }
      .p-menu .p-menuitem-link:hover { background: rgba(14,34,93,0.06) !important; }
      .p-menu .p-menuitem-icon { color: #0e225d !important; font-size: 0.9rem !important; }
      .p-menu .p-menuitem-text {
        color: #0e225d !important;
        font-weight: 600 !important;
        font-size: 0.85rem !important;
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    if (profile?.institute_id && profile?.branch_id) {
      fetchCourses();
      fetchBatches();
    }
  }, [page, search, profile]);

  // const fetchCourses = async () => {
  //   try {
  //     const response = await apiClient.get<{ success: boolean; data: Course[] }>('/v1/courses');
  //     if (response.data.success) setCourses(response.data.data);
  //   } catch (err) { console.error('Failed to fetch courses:', err); }
  // };
  const fetchCourses = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: Course[] }>('/v1/courses?status=active');
      if (response.data.success) setCourses(response.data.data);
    } catch (err) { console.error('Failed to fetch courses:', err); }
  };
  

  const fetchBatches = async () => {
    if (!profile?.institute_id || !profile?.branch_id) {
      setError('Institute or Branch information is missing.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ page: page.toString(), limit: '10', ...(search && { search }) });
      const response = await apiClient.get<ApiResponse>(`/v1/batches?${params}`);
      if (response.data.success) {
        setBatches(response.data.data);
        setPagination(response.data.pagination);
      } else { setError('Failed to load batches'); }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load batches');
    } finally { setLoading(false); }
  };

  const handleAddBatch = () => { setEditingBatch(null); setShowAddModal(true); };
  const handleEditBatch = (batch: Batch) => { setEditingBatch(batch); setShowAddModal(true); };
  const handleModalClose = () => { setShowAddModal(false); setEditingBatch(null); };
  const handleModalSuccess = () => {
    const wasEditing = !!editingBatch;
    toast.current?.show({
      severity: 'success', summary: 'Success',
      detail: wasEditing ? 'Batch updated successfully!' : 'Batch created successfully!', life: 3000
    });
    fetchBatches();
    setShowAddModal(false);
    setEditingBatch(null);
  };

  const handleDeleteBatch = (batchId: string, batchName: string) =>
    setDeleteDialog({ visible: true, batchId, batchName });

  const confirmDelete = async () => {
    try {
      await apiClient.delete(`/v1/batches/${deleteDialog.batchId}`);
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Batch deleted successfully', life: 3000 });
      fetchBatches();
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to delete batch', life: 4000 });
    }
  };

  const getMenuItems = (batch: Batch) => [
    { label: 'Edit', icon: 'pi pi-pencil', command: () => handleEditBatch(batch) },
    {
      label: batch.is_active ? 'Deactivate' : 'Activate',
      icon: batch.is_active ? 'pi pi-ban' : 'pi pi-check-circle',
      command: async () => {
        try {
          await apiClient.put(`/v1/batches/${batch.batch_id}`, { is_active: !batch.is_active });
          toast.current?.show({
            severity: 'success', summary: 'Success',
            detail: `Batch ${batch.is_active ? 'deactivated' : 'activated'} successfully`, life: 3000
          });
          fetchBatches();
        } catch (err: any) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to update batch status', life: 4000 });
        }
      }
    },
    { label: 'Delete', icon: 'pi pi-trash', command: () => handleDeleteBatch(batch.batch_id, batch.batch_name) }
  ];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return dateString; }
  };

  const getCourseName = (courseId: string) => {
    const course = courses.find(c => c.course_id === courseId);
    return course ? course.name : 'Unknown Course';
  };

  const getBatchDuration = (batch: Batch) =>
    `${batch.start_month} ${batch.start_year} — ${batch.end_month} ${batch.end_year}`;

  // ── Guard renders ─────────────────────────────────────────────────────────
  if (profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '16px' }}>
        <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ color: '#0e225d', fontSize: '0.9rem', fontWeight: '500' }}>Loading profile data...</p>
      </div>
    );
  }

  if (!profile?.institute_id || !profile?.branch_id) {
    return (
      <div style={{ padding: '24px' }}>
        <Message severity="warn" text="Institute or Branch information is missing. Please contact support." style={{ fontSize: '0.9rem' }} />
      </div>
    );
  }

  if (loading && batches.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: 'white', borderRadius: '16px', border: '1px solid #E5E7EB' }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '16px', color: '#0e225d', fontSize: '0.95rem', fontWeight: '500' }}>Loading batches...</p>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
      <Toast ref={toast} />

      <CustomConfirmDialog
        visible={deleteDialog.visible}
        onHide={() => setDeleteDialog({ visible: false, batchId: '', batchName: '' })}
        onConfirm={confirmDelete}
        title="Delete Batch"
        message={`Are you sure you want to delete "${deleteDialog.batchName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />

      {/* ── Hero Header ───────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(180deg, #0e225d 0%, #1a3370 50%, #0a1a3d 100%)',
        borderRadius: '20px', padding: '32px', marginBottom: '24px',
        color: 'white', position: 'relative', overflow: 'hidden',
        boxShadow: '2px 0 12px rgba(14, 34, 93, 0.3)'
      }}>
        {/* Decorative orbs */}
        <div style={{
          position: 'absolute', top: '-60px', right: '-60px',
          width: '220px', height: '220px',
          background: 'rgba(251,178,31,0.08)', borderRadius: '50%',
          filter: 'blur(50px)', pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute', bottom: '-40px', left: '30%',
          width: '140px', height: '140px',
          background: 'rgba(251,178,31,0.05)', borderRadius: '50%',
          filter: 'blur(40px)', pointerEvents: 'none'
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '4px 12px',
                background: 'rgba(251,178,31,0.18)', border: '1px solid rgba(251,178,31,0.35)',
                borderRadius: '20px', marginBottom: '12px',
                fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px',
                textTransform: 'uppercase', color: '#fbb21f'
              }}>
                <i className="pi pi-th-large" style={{ fontSize: '0.7rem' }}></i>
                Batch Management
              </div>
              <h1 style={{ margin: '0 0 8px 0', fontSize: '1.75rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>
                Batches
              </h1>
              <p style={{ margin: '0 0 12px 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
                Manage course batches and schedules
              </p>
              <div style={{ display: 'flex', gap: '20px', fontSize: '0.83rem', color: 'rgba(255,255,255,0.65)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className="pi pi-th-large" style={{ fontSize: '0.8rem' }} />
                  {pagination.total} Total Batches
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className="pi pi-check-circle" style={{ fontSize: '0.8rem', color: '#fbb21f' }} />
                  <span style={{ color: '#fbb21f', fontWeight: '600' }}>
                    {batches.filter(b => b.is_active).length}
                  </span> Active
                </span>
              </div>
            </div>

            {/* Add Batch button — gold */}
            <button
              onClick={handleAddBatch}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '12px 24px', background: '#fbb21f',
                border: 'none', borderRadius: '12px', color: '#0e225d',
                fontSize: '0.88rem', fontWeight: '700', cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(251,178,31,0.4)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', letterSpacing: '0.3px',
                alignSelf: 'flex-start'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(251,178,31,0.5)';
                e.currentTarget.style.background = '#ffc93c';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(251,178,31,0.4)';
                e.currentTarget.style.background = '#fbb21f';
              }}
            >
              <i className="pi pi-plus" style={{ fontSize: '0.9rem' }}></i>
              ADD BATCH
            </button>
          </div>
        </div>
      </div>

      {/* ── Search Card ───────────────────────────────────────────────────── */}
      <div style={{
        background: 'white', borderRadius: '16px', padding: '20px 24px',
        marginBottom: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)',
        border: '1px solid #E5E7EB'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <div style={{
            width: '4px', height: '18px',
            background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px'
          }} />
          <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0e225d', letterSpacing: '0.3px' }}>
            Search Batches
          </span>
        </div>
        <div style={{ position: 'relative' }}>
          <i className="pi pi-search" style={{
            position: 'absolute', left: '14px', top: '50%',
            transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '0.9rem', pointerEvents: 'none'
          }} />
          <InputText
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search batches by name..."
            style={{
              width: '100%', paddingLeft: '42px', height: '44px',
              border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.9rem',
              outline: 'none', transition: 'all 0.2s ease', color: '#0e225d'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#fbb21f';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(251,178,31,0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && (
        <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }} />
      )}

      {/* ── Empty State ───────────────────────────────────────────────────── */}
      {batches.length === 0 && !loading ? (
        <div style={{
          padding: '80px 20px', textAlign: 'center',
          background: 'white', borderRadius: '16px',
          border: '2px dashed #E5E7EB'
        }}>
          <div style={{
            width: '64px', height: '64px',
            background: 'rgba(14,34,93,0.06)', borderRadius: '16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <i className="pi pi-th-large" style={{ fontSize: '1.8rem', color: '#0e225d', opacity: 0.3 }} />
          </div>
          <h3 style={{ margin: '0 0 8px 0', color: '#0e225d', fontSize: '1.1rem', fontWeight: '700' }}>
            No batches found
          </h3>
          <p style={{ margin: '0 0 24px 0', color: '#9ca3af', fontSize: '0.9rem' }}>
            {search ? 'Try adjusting your search criteria' : 'Get started by adding your first batch'}
          </p>
          {!search && (
            <button
              onClick={handleAddBatch}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '10px 22px', background: '#fbb21f',
                border: 'none', borderRadius: '10px', color: '#0e225d',
                fontSize: '0.85rem', fontWeight: '700', cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(251,178,31,0.3)', transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <i className="pi pi-plus" style={{ fontSize: '0.85rem' }}></i>
              Add First Batch
            </button>
          )}
        </div>
      ) : (
        /* ── Batch Cards Grid ──────────────────────────────────────────── */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {batches.map((batch) => (
            <Card
              key={batch.batch_id}
              className="batch-card"
              style={{
                borderRadius: '20px', border: '1px solid #E5E7EB', background: 'white',
                boxShadow: '0 2px 8px rgba(14,34,93,0.06)', overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(14,34,93,0.14)';
                e.currentTarget.style.borderColor = 'rgba(251,178,31,0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.06)';
                e.currentTarget.style.borderColor = '#E5E7EB';
              }}
            >
              {/* Card Header — navy gradient */}
              <div style={{
                background: 'linear-gradient(180deg, #0e225d 0%, #1a3370 100%)',
                padding: '20px', borderRadius: '20px 20px 0 0',
                position: 'relative', overflow: 'hidden'
              }}>
                {/* Subtle orb */}
                <div style={{
                  position: 'absolute', top: '-30px', right: '-30px',
                  width: '100px', height: '100px',
                  background: 'rgba(251,178,31,0.08)', borderRadius: '50%',
                  filter: 'blur(20px)', pointerEvents: 'none'
                }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                  <div style={{ flex: 1, marginRight: '12px' }}>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '1.05rem', fontWeight: '700', color: 'white' }}>
                      {batch.batch_name}
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)', lineHeight: '1.4' }}>
                      {getCourseName(batch.course_id)}
                    </p>
                  </div>
                  {/* Status badge — gold active, muted inactive */}
                  <span style={{
                    padding: '4px 12px', borderRadius: '20px',
                    fontSize: '0.68rem', fontWeight: '700', flexShrink: 0, letterSpacing: '0.5px',
                    ...(batch.is_active
                      ? { background: '#fbb21f', color: '#0e225d' }
                      : { background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.2)' })
                  }}>
                    {batch.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>

              {/* Card Body */}
              <div style={{ padding: '20px' }}>

                {/* Schedule */}
                <div style={{ marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px solid #f3f4f6' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '0.68rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    Schedule
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '34px', height: '34px',
                      background: 'rgba(14,34,93,0.08)', borderRadius: '8px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <i className="pi pi-clock" style={{ color: '#0e225d', fontSize: '0.85rem' }} />
                    </div>
                    <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '600', color: '#0e225d' }}>
                      {batch.batch_time}
                    </p>
                  </div>
                </div>

                {/* Duration */}
                <div style={{ marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px solid #f3f4f6' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '0.68rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    Duration
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '34px', height: '34px',
                      background: 'rgba(251,178,31,0.12)', borderRadius: '8px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <i className="pi pi-calendar" style={{ color: '#b8860b', fontSize: '0.85rem' }} />
                    </div>
                    <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', color: '#1f2937' }}>
                      {getBatchDuration(batch)}
                    </p>
                  </div>
                </div>

                {/* Created */}
                <div style={{ marginBottom: '18px' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '0.68rem', color: '#9ca3af', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    Created
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '34px', height: '34px',
                      background: 'rgba(22,163,74,0.08)', borderRadius: '8px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <i className="pi pi-info-circle" style={{ color: '#15803d', fontSize: '0.85rem' }} />
                    </div>
                    <p style={{ margin: 0, fontSize: '0.83rem', fontWeight: '600', color: '#6b7280' }}>
                      {formatDate(batch.created_at)}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  {/* View Details — navy */}
                  <button
                    onClick={() => navigate(`/batches/${batch.batch_id}/details`)}
                    style={{
                      flex: 1, padding: '11px',
                      background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                      border: 'none', borderRadius: '10px',
                      color: 'white', fontSize: '0.83rem', fontWeight: '600',
                      cursor: 'pointer', transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      boxShadow: '0 2px 8px rgba(14,34,93,0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.2)';
                    }}
                  >
                    <i className="pi pi-eye" style={{ fontSize: '0.85rem' }} />
                    View Details
                  </button>

                  {/* Three-dot menu — subtle */}
                  <button
                    onClick={(e) => { e.stopPropagation(); menuRefs.current[batch.batch_id]?.toggle(e); }}
                    style={{
                      padding: '11px 14px',
                      background: '#F5F7FA', border: '1px solid #E5E7EB',
                      borderRadius: '10px', color: '#0e225d',
                      cursor: 'pointer', transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#E8EEF7';
                      e.currentTarget.style.borderColor = '#fbb21f';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#F5F7FA';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    }}
                  >
                    <i className="pi pi-ellipsis-v" style={{ fontSize: '0.9rem' }} />
                  </button>

                  <Menu
                    model={getMenuItems(batch)}
                    popup
                    ref={(el) => { menuRefs.current[batch.batch_id] = el; }}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      {pagination.pages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: '12px', marginTop: '24px', padding: '18px 24px',
          background: 'white', borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
            Page {pagination.page} of {pagination.pages} &nbsp;·&nbsp; {pagination.total} total batches
          </span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              style={{
                width: '38px', height: '38px',
                background: page === 1 ? '#F5F7FA' : 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                border: page === 1 ? '1px solid #E5E7EB' : 'none',
                borderRadius: '10px',
                color: page === 1 ? '#9ca3af' : '#fbb21f',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: page === 1 ? 'none' : '0 2px 8px rgba(14,34,93,0.2)',
                transition: 'all 0.2s ease'
              }}
            >
              <i className="pi pi-chevron-left" style={{ fontSize: '0.8rem' }} />
            </button>
            <button
              disabled={page === pagination.pages}
              onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
              style={{
                width: '38px', height: '38px',
                background: page === pagination.pages ? '#F5F7FA' : 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                border: page === pagination.pages ? '1px solid #E5E7EB' : 'none',
                borderRadius: '10px',
                color: page === pagination.pages ? '#9ca3af' : '#fbb21f',
                cursor: page === pagination.pages ? 'not-allowed' : 'pointer',
                fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: page === pagination.pages ? 'none' : '0 2px 8px rgba(14,34,93,0.2)',
                transition: 'all 0.2s ease'
              }}
            >
              <i className="pi pi-chevron-right" style={{ fontSize: '0.8rem' }} />
            </button>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ──────────────────────────────────────────────── */}
      <AddBatchModal
        visible={showAddModal}
        instituteId={profile?.institute_id || ''}
        branchId={profile?.branch_id || ''}
        courses={courses}
        editingBatch={editingBatch}
        onHide={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default Batches;

